import { useMemo, useState } from "react";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import type { ChartData, ChartDataset, ChartOptions } from "chart.js";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import {
  AREA_FILL_ALPHA,
  BAR_MAX_THICKNESS,
  BAR_RADIUS,
  CHART_FONT_FAMILY,
  CHART_FONT_SIZE,
  CHART_GRID_TOKEN,
  CHART_SURFACE_TOKEN,
  CHART_TICK_TOKEN,
  FILL_GAP_WIDTH,
  LINE_WIDTH,
  PERCENT_SCALE,
  POINT_HOVER_RADIUS,
  POINT_RADIUS,
  SERIES_KINDS,
  SERIES_VALUE_FORMATS,
} from "@/components_v2/Charts/Series/constants";
import { resolveCssColor, withAlpha } from "@/components_v2/Charts/utils";
import type {
  BaseSeriesChartProps,
  ChartSeries,
} from "@/components_v2/Charts/types";

import "../styles.css";

type MixedType = "bar" | "line";
type MixedDataset = ChartDataset<MixedType, (number | null)[]>;

/** Bar, line and area series on one value axis, with a table view. */
const BaseSeriesChart = ({
  title,
  categoryLabel,
  labels,
  series,
  height,
  stacked = false,
  valueFormat = SERIES_VALUE_FORMATS.COUNT,
  indexAxis = "x",
  getTooltipFooter,
  className,
}: BaseSeriesChartProps) => {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);

  const isPercent = valueFormat === SERIES_VALUE_FORMATS.PERCENT;

  const formatValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) return t("charts.noValue");
    return isPercent
      ? t("charts.percent", { percent: Math.round(value * PERCENT_SCALE) })
      : value.toLocaleString();
  };

  const data = useMemo((): ChartData<MixedType, (number | null)[]> => {
    const surface = resolveCssColor(CHART_SURFACE_TOKEN);
    let previousAreaIndex: number | null = null;

    const datasets = series.map((item: ChartSeries, index): MixedDataset => {
      const color = resolveCssColor(item.color);
      const kind = item.kind ?? SERIES_KINDS.BAR;

      if (kind === SERIES_KINDS.BAR) {
        return {
          type: "bar",
          label: item.label,
          data: item.data,
          backgroundColor: color,
          hoverBackgroundColor: color,
          borderColor: surface,
          borderWidth: stacked ? FILL_GAP_WIDTH : 0,
          borderRadius: BAR_RADIUS,
          borderSkipped: "start",
          maxBarThickness: BAR_MAX_THICKNESS,
          stack: "bars",
          order: 2,
        };
      }

      const fill =
        kind === SERIES_KINDS.AREA
          ? stacked && previousAreaIndex !== null
            ? previousAreaIndex
            : "origin"
          : false;
      if (kind === SERIES_KINDS.AREA) previousAreaIndex = index;

      return {
        type: "line",
        label: item.label,
        data: item.data,
        borderColor: color,
        backgroundColor:
          kind === SERIES_KINDS.AREA
            ? withAlpha(color, AREA_FILL_ALPHA)
            : color,
        pointBackgroundColor: color,
        pointBorderColor: surface,
        borderWidth: LINE_WIDTH,
        pointRadius: POINT_RADIUS,
        pointHoverRadius: POINT_HOVER_RADIUS,
        fill,
        spanGaps: false,
        stack: kind === SERIES_KINDS.AREA ? "areas" : item.key,
        order: 1,
      };
    });

    return { labels, datasets };
  }, [labels, series, stacked]);

  const options = useMemo((): ChartOptions<MixedType> => {
    const gridColor = resolveCssColor(CHART_GRID_TOKEN);
    const tickColor = resolveCssColor(CHART_TICK_TOKEN);
    const font = { family: CHART_FONT_FAMILY, size: CHART_FONT_SIZE };
    const valueAxis = {
      stacked,
      beginAtZero: true,
      ...(isPercent ? { min: 0, max: 1 } : {}),
      grid: { color: gridColor },
      border: { display: false },
      ticks: {
        color: tickColor,
        font,
        ...(isPercent
          ? {
              stepSize: 0.25,
              callback: (value: number | string) => formatValue(Number(value)),
            }
          : { precision: 0 }),
      },
    };
    const categoryAxis = {
      stacked,
      grid: { display: false },
      border: { color: gridColor },
      ticks: { color: tickColor, font, autoSkip: true, maxRotation: 0 },
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis,
      interaction: { mode: "index", intersect: false },
      scales:
        indexAxis === "y"
          ? { x: valueAxis, y: categoryAxis }
          : { x: categoryAxis, y: valueAxis },
      plugins: {
        datalabels: { display: false },
        legend: {
          display: series.length > 1,
          position: "bottom",
          labels: { usePointStyle: true, color: tickColor, font },
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${formatValue(context.parsed[indexAxis === "y" ? "x" : "y"])}`,
            footer: (items) =>
              items[0] && getTooltipFooter
                ? (getTooltipFooter(items[0].dataIndex) ?? "")
                : "",
          },
        },
      },
    };
  }, [stacked, isPercent, indexAxis, series.length, getTooltipFooter, t]);

  return (
    <div
      className={`series-chart flex flex-col gap-200 full-width ${className ?? ""}`}
    >
      <div className="flex justify-end">
        <BaseButton
          variant={BUTTON_VARIANTS.LINK}
          size={BUTTON_SIZES.EXTRA_SMALL}
          onClick={() => setShowTable((value) => !value)}
          ariaPressed={showTable}
        >
          {showTable ? t("charts.showChart") : t("charts.showTable")}
        </BaseButton>
      </div>

      {showTable ? (
        <div className="series-chart-table-wrapper">
          <table className="series-chart-table body-s-regular full-width">
            <caption className="series-chart-caption">{title}</caption>

            <thead>
              <tr>
                <th scope="col">{categoryLabel}</th>

                {series.map((item) => (
                  <th key={item.key} scope="col">
                    {item.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {labels.map((label, index) => (
                <tr key={`${label}-${index}`}>
                  <th scope="row">{label}</th>

                  {series.map((item) => (
                    <td key={item.key}>{formatValue(item.data[index])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          className="series-chart-canvas full-width"
          style={{ height: `${height}px` }}
        >
          <Chart
            type="bar"
            data={data}
            options={options}
            role="img"
            aria-label={title}
          />
        </div>
      )}
    </div>
  );
};

export default BaseSeriesChart;
